import { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { translationsForPath } from '@/i18n/translations';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * The fallback screen. It lives outside `LocaleProvider` (the boundary wraps
 * the router in App.tsx, so an error thrown by a provider still gets caught),
 * which means `useLocale()` is not available here — the locale comes from the
 * URL instead, so an en/de/ru visitor is not answered in Slovenian.
 */
export function ErrorFallback({
  error,
  errorInfo,
  onReset,
}: {
  error?: Error;
  errorInfo?: ErrorInfo;
  onReset: () => void;
}) {
  const t = translationsForPath(
    typeof window !== 'undefined' ? window.location.pathname : '/',
  ).errorBoundary;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 px-4">
      <div className="surface-card max-w-md w-full p-8 text-center animate-fade-in-up border-0 bg-gradient-card">
        <p className="section-eyebrow mb-4">{t.eyebrow}</p>
        <AlertTriangle className="h-14 w-14 text-destructive mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-foreground mb-2">{t.title}</h1>
        <p className="text-muted-foreground mb-8">{t.desc}</p>

        <div className="space-y-3">
          <Button onClick={onReset} className="w-full" variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            {t.retry}
          </Button>

          <Button onClick={() => window.location.reload()} className="w-full">
            {t.reload}
          </Button>
        </div>

        {process.env.NODE_ENV === 'development' && error && (
          <details className="mt-6 text-left">
            <summary className="cursor-pointer text-sm text-muted-foreground mb-2">
              {t.devDetails}
            </summary>
            <div className="bg-muted/60 p-4 rounded-xl text-xs font-mono overflow-auto">
              <div className="mb-2">
                <strong>Error:</strong> {error.message}
              </div>
              {errorInfo && (
                <div>
                  <strong>Stack:</strong>
                  <pre className="whitespace-pre-wrap mt-1">
                    {errorInfo.componentStack}
                  </pre>
                </div>
              )}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ error, errorInfo });

    // Log error to external service in production
    if (process.env.NODE_ENV === 'production') {
      // TODO: Add error reporting service (e.g., Sentry)
      console.error('Error details:', { error, errorInfo });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
